import { useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import { ValidateCurrentToken } from "../../helpers";

export type SriSyncNotificationTipo =
  | "recibidos"
  | "retenciones"
  | "emitidos"
  | "anulados"
  | "factura";

export interface SriSyncNotificationI {
  jobId: string;
  tipo: SriSyncNotificationTipo;
  status: "COMPLETED" | "FAILED";
  resultado: { encontrados: number; nuevos: number } | null;
  error: string | null;
  timestamp: string;
  leida: boolean;
  // aviso de factura de una venta: texto armado por el backend y el id
  // (cifrado) de la venta para navegar a su detalle
  mensaje?: string;
  referenciaId?: string;
}

interface UseSriSyncNotificationsProps {
  clientId: string;
  branchId: string | null;
  role: string;
}

const TIPO_LABEL: Record<SriSyncNotificationTipo, string> = {
  recibidos: "Facturas recibidas",
  retenciones: "Retenciones",
  emitidos: "Comprobantes emitidos",
  anulados: "Anulados",
  factura: "Factura",
};

/**
 * Escucha notificaciones en tiempo real de tareas SRI completadas/fallidas
 * — mismo mecanismo (rooms por cliente/sucursal) que useStockSocket, sin
 * infraestructura nueva del lado del servidor.
 */
export const useSriSyncNotifications = ({
  clientId,
  branchId,
  role,
}: UseSriSyncNotificationsProps) => {
  const socketRef = useRef<Socket | null>(null);
  const [notificaciones, setNotificaciones] = useState<SriSyncNotificationI[]>([]);

  useEffect(() => {
    if (!clientId) return;

    // el backend decide a qué rooms entra este socket a partir del JWT del
    // handshake — el cliente no elige room (multi-tenant).
    const token = ValidateCurrentToken();
    if (typeof token !== "string") return;

    socketRef.current = io(
      process.env.REACT_APP_URL_BACKEND ?? "http://localhost:2000",
      { transports: ["websocket"], auth: { token } },
    );

    socketRef.current.on("sri-sync-notification", (payload: Omit<SriSyncNotificationI, "leida">) => {
      setNotificaciones((prev) => [{ ...payload, leida: false }, ...prev].slice(0, 30));
    });

    // estado final de la factura SRI de una venta (autorizada / con error)
    socketRef.current.on(
      "sale-sri-notification",
      (p: { saleId: string; status: "COMPLETED" | "FAILED"; mensaje: string; error: string | null; timestamp: string }) => {
        setNotificaciones((prev) =>
          [
            {
              jobId: `${p.saleId}-${p.status}`,
              tipo: "factura" as const,
              status: p.status,
              resultado: null,
              error: p.error,
              timestamp: p.timestamp,
              mensaje: p.mensaje,
              referenciaId: p.saleId,
              leida: false,
            },
            ...prev,
          ].slice(0, 30),
        );
      },
    );

    return () => {
      socketRef.current?.disconnect();
    };
  }, [clientId, branchId, role]);

  const marcarTodasLeidas = () => {
    setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
  };

  const marcarLeida = (jobId: string) => {
    setNotificaciones((prev) =>
      prev.map((n) => (n.jobId === jobId ? { ...n, leida: true } : n)),
    );
  };

  const noLeidas = notificaciones.filter((n) => !n.leida).length;

  const formatearMensaje = (n: SriSyncNotificationI): string => {
    if (n.mensaje) return n.mensaje;
    const label = TIPO_LABEL[n.tipo] ?? n.tipo;
    if (n.status === "COMPLETED") {
      return `${label}: ${n.resultado?.nuevos ?? 0} nuevo(s) de ${n.resultado?.encontrados ?? 0} encontrado(s)`;
    }
    return `${label}: falló — ${n.error ?? "error desconocido"}`;
  };

  return { notificaciones, noLeidas, marcarTodasLeidas, marcarLeida, formatearMensaje };
};